import {
  apiErrorSchema,
  ownProfileOptionsSchema,
  ownProfileSchema,
  type OwnProfile,
  type UpdateOwnBirthdayRequest,
  type UpdateOwnProfileRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError, apiFetch, API_BASE_URL } from '@/lib/api';
import { meQueryKey } from '@/platform/auth/use-me';

export const ownProfileQueryKey = ['users', 'me'] as const;
export const ownProfileOptionsQueryKey = ['users', 'me', 'profile-options'] as const;

export function useOwnProfile(enabled = true) {
  return useQuery({
    queryKey: ownProfileQueryKey,
    queryFn: () => apiFetch('/users/me', ownProfileSchema),
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useOwnProfileOptions() {
  return useQuery({
    queryKey: ownProfileOptionsQueryKey,
    queryFn: () => apiFetch('/users/me/profile-options', ownProfileOptionsSchema),
  });
}

export function useUpdateOwnProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOwnProfileRequest) =>
      apiFetch('/users/me', ownProfileSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (profile: OwnProfile) => {
      queryClient.setQueryData(ownProfileQueryKey, profile);
    },
  });
}

export function useUploadProfilePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${API_BASE_URL}/users/me/profile-photo`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const parsed = apiErrorSchema.safeParse(body);
        throw new ApiRequestError(
          parsed.success
            ? parsed.data
            : {
                statusCode: response.status,
                code: 'UNKNOWN',
                message: 'Der Upload ist fehlgeschlagen.',
                timestamp: new Date().toISOString(),
                path: '/users/me/profile-photo',
              },
        );
      }
      return ownProfileSchema.parse(body);
    },
    onSuccess: (profile: OwnProfile) => {
      queryClient.setQueryData(ownProfileQueryKey, profile);
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useRemoveProfilePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/users/me/profile-photo', ownProfileSchema, {
        method: 'DELETE',
      }),
    onSuccess: (profile: OwnProfile) => {
      queryClient.setQueryData(ownProfileQueryKey, profile);
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useUpdateOwnBirthday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateOwnBirthdayRequest) =>
      apiFetch('/users/me/birthday', ownProfileSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (profile: OwnProfile) => {
      queryClient.setQueryData(ownProfileQueryKey, profile);
    },
  });
}
